import { createContext, useContext, useState, ReactNode, useCallback } from "react";

interface SavedItemsContextType {
  savedCommunities: string[];
  savedEvents: string[]; // "communityId:eventId"
  savedOpportunities: string[];
  savedBrands: string[];
  toggleSavedCommunity: (id: string) => void;
  toggleSavedEvent: (key: string) => void;
  toggleSavedOpportunity: (id: string) => void;
  toggleSavedBrand: (id: string) => void;
  isCommunityIdSaved: (id: string) => boolean;
  isEventSaved: (key: string) => boolean;
  isOpportunitySaved: (id: string) => boolean;
  isBrandSaved: (id: string) => boolean;
}

const SavedItemsContext = createContext<SavedItemsContextType>({
  savedCommunities: [],
  savedEvents: [],
  savedOpportunities: [],
  savedBrands: [],
  toggleSavedCommunity: () => {},
  toggleSavedEvent: () => {},
  toggleSavedOpportunity: () => {},
  toggleSavedBrand: () => {},
  isCommunityIdSaved: () => false,
  isEventSaved: () => false,
  isOpportunitySaved: () => false,
  isBrandSaved: () => false,
});

function useToggleSet(initial: string[] = []) {
  const [items, setItems] = useState<string[]>(initial);
  const toggle = useCallback((id: string) => {
    setItems((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  }, []);
  const has = useCallback((id: string) => items.includes(id), [items]);
  return { items, toggle, has };
}

export const SavedItemsProvider = ({ children }: { children: ReactNode }) => {
  const communities = useToggleSet(["paris-runners-club", "yoga-mindfulness-paris"]);
  const events = useToggleSet(["paris-runners-club:evt-001"]);
  const opportunities = useToggleSet(["opp-001"]);
  const brands = useToggleSet(["hoka-running"]);

  return (
    <SavedItemsContext.Provider
      value={{
        savedCommunities: communities.items,
        savedEvents: events.items,
        savedOpportunities: opportunities.items,
        savedBrands: brands.items,
        toggleSavedCommunity: communities.toggle,
        toggleSavedEvent: events.toggle,
        toggleSavedOpportunity: opportunities.toggle,
        toggleSavedBrand: brands.toggle,
        isCommunityIdSaved: communities.has,
        isEventSaved: events.has,
        isOpportunitySaved: opportunities.has,
        isBrandSaved: brands.has,
      }}
    >
      {children}
    </SavedItemsContext.Provider>
  );
};

export const useSavedItems = () => useContext(SavedItemsContext);
